import { z } from 'zod';
import { env } from '@/lib/config/env';
import { type DiagnosticValueClass, diagnosticValueClass } from './diagnostic-validation';

const ERROR_CODES: ReadonlySet<string> = new Set([
  'OFFLINE',
  'TIMEOUT',
  'TLS_FAIL',
  'FETCH_FAILED',
  'WSS_OPEN_FAILED',
  'JWT_EXPIRED',
  'JWT_INVALID',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NATS_AUTH_REJECTED',
  'BAD_REQUEST',
  'UNPROCESSABLE',
  'ZOD_PARSE_FAILED',
  'INTERNAL',
  'BAD_GATEWAY',
  'SERVICE_UNAVAILABLE',
  'NATS_NO_RESPONDERS',
  'NATS_DISCONNECTED',
  'SLOW_CONSUMER',
  'SUBJECT_REJECTED',
  'PROTO_DECODE_FAILED',
  'JSON_PARSE_FAILED',
  'SHAPE_MISMATCH',
  'UNKNOWN_ENUM',
  'TOO_MANY_REQUESTS',
  'NATS_SLOW_CONSUMER',
  'UPSTREAM_ERROR',
  'STALE_OPTIMISTIC',
  'CHAIN_CONFIG_UNAVAILABLE',
]);

export interface LiveEventDiagnosticContext {
  timestamp: string;
  service: 'frontend';
  environment: string | undefined;
  version: string;
}

export interface LiveEventErrorFields {
  errorClass: DiagnosticValueClass | 'SdkError' | 'Error';
  errorCode?: string | undefined;
  httpStatus?: number | undefined;
  correlationId?: string | undefined;
  requestId?: string | undefined;
}
interface ErrorMetadata {
  name?: 'SdkError' | undefined;
  code?: string | null | undefined;
  httpStatus?: number | undefined;
  correlationId?: string | undefined;
  requestId?: string | undefined;
}

const requestIdentitySchema: z.ZodType<string | undefined, z.ZodTypeDef, unknown> = z
  .string()
  .regex(/^(?:[0-9a-f]{8}-[0-9a-f-]{27}|[0-9A-HJKMNP-TV-Z]{26})$/i)
  .optional()
  .catch(undefined);
const errorMetadataSchema: z.ZodType<ErrorMetadata, z.ZodTypeDef, unknown> = z.object({
  name: z.literal('SdkError').optional().catch(undefined),
  code: z.string().nullish().catch('UNCLASSIFIED'),
  httpStatus: z.number().min(100).max(599).optional().catch(undefined),
  correlationId: requestIdentitySchema,
  requestId: requestIdentitySchema,
});

export function liveEventDiagnosticContext(): LiveEventDiagnosticContext {
  return {
    timestamp: new Date().toISOString(),
    service: 'frontend',
    environment: env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.NODE_ENV,
    version: env.NEXT_PUBLIC_APP_VERSION ?? 'dev',
  };
}

export function safeLiveEventError<T>(error: T): LiveEventErrorFields {
  const parsed = errorMetadataSchema.safeParse(error);
  if (!parsed.success) return unparsedErrorFields(error);
  const value = parsed.data;
  const code = value.code ?? (error instanceof Error ? error.message : undefined);
  return {
    errorClass:
      value.name === 'SdkError' ? 'SdkError' : error instanceof Error ? 'Error' : 'object',
    errorCode: code !== undefined && ERROR_CODES.has(code) ? code : 'UNCLASSIFIED',
    httpStatus: value.httpStatus,
    correlationId: value.correlationId,
    requestId: value.requestId,
  };
}

function unparsedErrorFields<T>(error: T): LiveEventErrorFields {
  const errorClass = diagnosticValueClass(z.getParsedType(error));
  return errorClass === 'object' && error !== null
    ? { errorClass, errorCode: 'UNCLASSIFIED' }
    : { errorClass };
}
